/**
 * <similar-shopify-stones> — Slider of similar Shopify-product stones.
 *
 * Queries the moissanites collection via Storefront API for products matching
 * `stone_shape`, excludes the current product, renders <stone-card> elements
 * inside a Swiper slider (mirrors legacy stone-slide structure).
 *
 * Required attrs: shape, current-handle. Optional: collection-handle, limit, title, stone-type.
 */

const QUERY = `
  query similarStones($handle: String!, $first: Int!, $filters: [ProductFilter!]) {
    collection(handle: $handle) {
      products(first: $first, filters: $filters, sortKey: BEST_SELLING) {
        edges { node { handle } }
      }
    }
  }
`;

const SWIPER_CONFIG = {
  slidesPerView: 1.6,
  spaceBetween: 20,
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  breakpoints: {
    768:  { slidesPerView: 3 },
    1024: { slidesPerView: 4 },
  },
};

if (!customElements.get('similar-shopify-stones')) {
  class SimilarShopifyStones extends HTMLElement {
    async connectedCallback() {
      if (this._loaded) return;
      this._loaded = true;

      this.shape = this.getAttribute('shape') || '';
      this.currentHandle = this.getAttribute('current-handle') || '';
      this.collectionHandle = this.getAttribute('collection-handle') || 'moissanites-1';
      this.limit = parseInt(this.getAttribute('limit'), 10) || 12;
      this.title = this.getAttribute('title') || 'Similar Stones';
      this.stoneType = this.getAttribute('stone-type') || 'Moissanite';

      if (!this.shape) return;

      try {
        await this.waitForApi();
        const handles = await this.fetchHandles();
        if (!handles.length) {
          this.classList.add('hidden');
          return;
        }
        this.renderSlider(handles);
        this.classList.remove('hidden', 'opacity-0');
        await this.waitForSwiper();
        this.initSwiper();
      } catch (err) {
        console.warn('similar-shopify-stones failed:', err);
      }
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    waitForApi() {
      return new Promise((resolve) => {
        if (window.theme?.shopify?.executeQuery) return resolve();
        const t = setInterval(() => {
          if (window.theme?.shopify?.executeQuery) { clearInterval(t); resolve(); }
        }, 50);
        setTimeout(() => { clearInterval(t); resolve(); }, 5000);
      });
    }

    waitForSwiper() {
      return new Promise((resolve) => {
        if (typeof Swiper !== 'undefined') return resolve();
        const t = setInterval(() => {
          if (typeof Swiper !== 'undefined') { clearInterval(t); resolve(); }
        }, 100);
        setTimeout(() => { clearInterval(t); resolve(); }, 5000);
      });
    }

    async fetchHandles() {
      const filters = [
        { productMetafield: { namespace: 'custom', key: 'stone_type',  value: this.stoneType } },
        { productMetafield: { namespace: 'custom', key: 'stone_shape', value: this.shape } },
      ];
      const data = await window.theme.shopify.executeQuery(QUERY, {
        handle: this.collectionHandle,
        first: this.limit + 1,
        filters,
      });
      const edges = data?.collection?.products?.edges || [];
      return edges
        .map(e => e.node.handle)
        .filter(h => h !== this.currentHandle)
        .slice(0, this.limit);
    }

    renderSlider(handles) {
      const slides = handles.map(h =>
        `<div class="swiper-slide"><stone-card data-handle="${h}"></stone-card></div>`
      ).join('');

      this.className = `${this.className} overflow-hidden block w-full`.trim();

      this.innerHTML = `
        <div class="flex items-end justify-between gap-10 px-4 mb-5 mt-5 tablet:justify-start">
          <h2 class="stone-slide__title text-2xl tablet:text-4xl inline-block">${this.title}</h2>
        </div>
        <div class="swiper overflow-visible px-4">
          <div class="swiper-wrapper">${slides}</div>
          <div class="swiper-button-prev hidden -top-[40px] right-[70px] left-auto tablet:flex"></div>
          <div class="swiper-button-next hidden -top-[40px] right-[20px] left-auto tablet:flex"></div>
        </div>
      `;
    }

    initSwiper() {
      if (this.swiper) return;
      const el = this.querySelector('.swiper');
      if (el) this.swiper = new Swiper(el, SWIPER_CONFIG);
    }
  }
  customElements.define('similar-shopify-stones', SimilarShopifyStones);
}
